import { Badge } from '@mantine/core'

/**
 * One colour vocabulary for every status in the app, so the same word never
 * means two things on two screens.
 *
 * The BOM statuses are the Hebrew literals stored in the database
 * (`BOMStatus.DRAFT = 'טיוטה'`), not translations applied here — the value in
 * the column *is* the label.
 */
const COLOURS: Record<string, string> = {
  // BOM
  'טיוטה': 'blue',
  'בהזמנה': 'teal',
  // Purchase orders
  OPEN: 'blue',
  SUBMITTED: 'indigo',
  PARTIAL: 'yellow',
  RECEIVED: 'green',
  CLOSED: 'gray',
  CANCELLED: 'red',
  // Issuances
  DRAFT: 'blue',
  ISSUED: 'green',
  CORRECTED: 'orange',
  // Replacement plans (DRAFT is shared with issuances above; CANCELLED is
  // shared with purchase orders below — same colour, same word, same meaning
  // every time it appears in this app).
  APPROVED: 'teal',
  CONVERTED: 'grape',
  // Catalog, customers, budgets, dealers
  ACTIVE: 'green',
  EOS: 'yellow',
  EOL: 'gray',
  ARCHIVED: 'gray',
  FROZEN: 'cyan',
}

// Deliberately no lowercase aliases. Every status in this app is an uppercase
// enum value, and `DealerDAO.get_all` / the ACTIVE filters compare literally —
// so a lowercase entry here would give a wrong value a healthy green badge
// while the row was invisible to the query. An unknown status renders grey,
// which is the signal that something wrote a value nothing else recognises.

interface Props {
  value: string
  size?: string
  /**
   * Shown instead of `value`, e.g. a Hebrew gloss of an English status code
   * (replacement plans: `DRAFT` stored, "טיוטה" shown). `value` still drives
   * the colour lookup above — the stored code, never a translation of it, so
   * swapping the displayed word can never also swap the colour vocabulary.
   * Omit this for every status that already *is* its own Hebrew label (BOMs),
   * which is most of them.
   */
  label?: string
}

export function StatusBadge({ value, size = 'sm', label }: Props) {
  if (!value) return <>—</>
  return (
    // The wrapper span carries the class, not the Badge.
    //
    // Mantine 9's Badge forwards **neither** `className` nor `styles` to its
    // root — verified in the DOM, where the rendered element was only
    // `m_347db0ec mantine-Badge-root` with no trace of either. Both attempts
    // therefore did nothing at all, silently, which is a good reminder that a
    // style that "should" apply is worth checking in the rendered output.
    //
    // See `.qtapp-status-badge` in index.css for why the truncation has to go.
    <span className="qtapp-status-badge">
      <Badge size={size as never} variant="light" color={COLOURS[value] ?? 'gray'}>
        {label ?? value}
      </Badge>
    </span>
  )
}
